  import React, { useState, useEffect, useMemo } from "react";
  import PropTypes from "prop-types";
  import {
    REACT_APP_AUTH_USERNAME,
    REACT_APP_AUTH_PASSWORD,
    REACT_APP_TRIPLE_STORE_URL,
  } from "../utils/env";
  import {
    // Layout
    Box,
    Grid,
    Tab,
    CardHeader,
    Card,
    CardContent,
    Typography,
    Chip,
    Divider,
    CircularProgress,
    // Form Controls
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
    ButtonGroup,
    Button
  } from "@mui/material";
  import {
    Euro as EuroIcon,
    TrendingUp,
    ScatterPlot,
    Assessment,
    Nature
  } from "@mui/icons-material";
  import TabList from "@mui/lab/TabList";
  import { InfoOutlined } from "@mui/icons-material";
  import TabContext from "@mui/lab/TabContext";
  import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
  import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
  import { DataGrid, GridToolbar } from "@mui/x-data-grid";
  import Highcharts from "highcharts";
  import HighchartsReact from "highcharts-react-official";
  import Exporting from "highcharts/modules/exporting";
  import OfflineExporting from "highcharts/modules/offline-exporting";
  import { cropNormalizer } from "../utils/cropMatcher";
  require("highcharts/modules/accessibility")(Highcharts);

  // Initialize modules once (safe for SSR)
  if (typeof Highcharts === "object") {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
  }

  // ─── Aggregate-year handling ───────────────────────────────────────────────
  // The triple store stores a convenience entry under AGGREGATE_YEAR (2026)
  // that contains the average/aggregate of the three cultivation years 2023-2025.
  // It must NOT be mixed with per-year data in "All Years" aggregations.
  const AGGREGATE_YEAR     = 2026;                    // numeric — the year key used in the triple store
  const AGGREGATE_YEAR_STR = String(AGGREGATE_YEAR);  // string  — used by tabs that keep years as strings
  const AGGREGATE_LABEL    = "📊 All Years\nAggregate"; // human-readable: what years it covers
  // ──────────────────────────────────────────────────────────────────────────

  const formatDisplayUnit = (unit) => {
    if (!unit) return unit;

    const trimmedUnit = String(unit).trim();
    if (!trimmedUnit) return trimmedUnit;

    const baseUnit = trimmedUnit
      .replace(/\s+per\s+.+$/i, "")
      .replace(/^((?:euro|eur|\u20ac))\s*\/\s*.+$/i, "$1")
      .trim();

    return /^euro$/i.test(baseUnit) ? "Euro" : baseUnit || trimmedUnit;
  };

  const getImpactColor = (value, higherIsBetter) => {
    const isPositiveImpact = higherIsBetter ? value > 0 : value <= 0;
    return isPositiveImpact ? "#4caf50" : "#f44336";
  };

  const ImpactLevelIndicator = ({ impactLevel }) => {
    // Normalize impact level to 1-5 scale
    return (
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 1 }}>
        {[1, 2, 3, 4, 5].map((level) => (
          <Box
            key={level}
            sx={{
              width: 20,
              height: 8,
              borderRadius: 1,
              backgroundColor:
                level <= impactLevel
                  ? // Color gradient from red to yellow to green based on level
                    "#464444ff"
                  : "#dbd8d8ff", // Gray for inactive boxes
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          />
        ))}
        <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
          Impact Level
        </Typography>
      </Box>
    );
  };

  function TabPanel({ children, value, index }) {
    return (
      <div role="tabpanel" hidden={value !== index}>
        {value === index && (
          <Box sx={{ pt: 2, minHeight: "auto", overflow: "visible" }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  function CategoryImpactTab({ data, selectedCrop }) {
    const [showPositive, setShowPositive] = useState(true);
    const [selectedYear, setSelectedYear] = useState("all");

    const parseDataForImpacts = (data, cropFilter) => {
      if (!data?.results?.bindings || !Array.isArray(data.results.bindings)) {
        return [];
      }

      // const normalizedFilter = cropFilter?.toLowerCase();

      return data.results.bindings
        .filter((item) => {
          if (item.name?.value === "NET BENEFIT") return false;

          if (cropFilter && item.product?.value) {
            // cropNormalizer.isMatch compares:
            // - cropFilter (from FilterContent, e.g., "potato") 
            // - item.product.value (from data, e.g., "potatoes")
            // Returns true if they match according to our rules
            if (!cropNormalizer.isMatch(item.product.value, cropFilter)) {
              return false; // Filter out this item if crops don't match
            }
          }
          return true;
        })
        .map((item) => ({
          name: item.name?.value,
          product: item.product?.value,
          year: item.year?.value || "Unknown",
          unit: item.unit?.value,
          absDiff: parseFloat(item.absDiff?.value),
          percDiff: parseFloat(item.percDiff?.value),
          title: item.title?.value,
          subtitle: item.subtitle?.value,
          higherIsBetter: item.higherIsBetter?.value === "true",
          impactLevel: parseFloat(item.impactLevel?.value),
        }));
    };

    const parsedData = parseDataForImpacts(data, selectedCrop);

    // Get unique years and sort them
    const years = [...new Set(parsedData.map((item) => item.year))].sort();

    // Separate the per-year entries from the convenience aggregate entry
    const individualYears    = years.filter(y => y !== AGGREGATE_YEAR_STR);
    const hasAggregateSummary = years.includes(AGGREGATE_YEAR_STR);

    // Categorize impacts by year
    const categorizeImpactsByYear = (parsedData) => {
      const yearGroups = {};

      parsedData.forEach((item) => {
        const year = item.year;

        if (!yearGroups[year]) {
          yearGroups[year] = { positive: [], negative: [] };
        }

        const value = !isNaN(item.absDiff) ? item.absDiff : item.percDiff;
        const isPositiveImpact = item.higherIsBetter ? value > 0 : value <= 0;

        if (isPositiveImpact) {
          yearGroups[year].positive.push(item);
        } else {
          yearGroups[year].negative.push(item);
        }
      });

      return yearGroups;
    };

    const yearGroups = categorizeImpactsByYear(parsedData);

    // Get data for selected year or aggregate all years
    const getDisplayData = () => {
      // Multi-year summary entry: return 2026 aggregate directly
      if (selectedYear === AGGREGATE_YEAR_STR) {
        return yearGroups[AGGREGATE_YEAR_STR] || { positive: [], negative: [] };
      }
      if (selectedYear === "all") {
        // Aggregate only individual years — exclude the rolled-up summary entry
        const aggregated = { positive: [], negative: [] };
        Object.entries(yearGroups).forEach(([year, yearData]) => {
          if (year !== AGGREGATE_YEAR_STR) {
            aggregated.positive.push(...yearData.positive);
            aggregated.negative.push(...yearData.negative);
          }
        });
        return aggregated;
      }
      return yearGroups[selectedYear] || { positive: [], negative: [] };
    };

    const displayData = getDisplayData();

    // Update how we find netBenefit from SPARQL results - filter by crop and selected year if specified
    const getNetBenefit = () => {
      const benefits = data?.results?.bindings?.filter((item) => {
        if (item.name?.value !== "NET BENEFIT") return false;

        // Filter by crop if specified
        if (
          selectedCrop &&
          item.product?.value?.toLowerCase() !== selectedCrop.toLowerCase()
        ) {
          return false;
        }

        // When user explicitly selects the multi-year summary, only return that entry
        if (selectedYear === AGGREGATE_YEAR_STR) {
          return item.year?.value === AGGREGATE_YEAR_STR;
        }

        // For individual-year or all-years views, always exclude the aggregate entry
        // to avoid double-counting
        if (item.year?.value === AGGREGATE_YEAR_STR) return false;

        // Filter by selected year when not "all"
        if (selectedYear !== "all" && item.year?.value !== selectedYear) {
          return false;
        }

        return true;
      });

      // If viewing all individual years, sum up the net benefits
      if (selectedYear === "all" && benefits && benefits.length > 0) {
        const totalBenefit = benefits.reduce((sum, item) => {
          return sum + parseFloat(item.absDiff?.value || 0);
        }, 0);

        // Return an object matching the structure of a single benefit
        return {
          absDiff: { value: totalBenefit.toString() }
        };
      }

      return benefits?.[0]; // Return first matching NET BENEFIT for specific year
    };

    const netBenefit = getNetBenefit();

    // Add loading state handling
    if (!data) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        {/* Year Filter */}
        {(individualYears.length > 1 || hasAggregateSummary) && (
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <MenuItem value="all">All Years</MenuItem>
                {individualYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
                {hasAggregateSummary && (
                  <MenuItem value={AGGREGATE_YEAR_STR} sx={{ whiteSpace: "pre-line" }}>
                    {AGGREGATE_LABEL}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {selectedYear === AGGREGATE_YEAR_STR && (
            <Chip
              label={"Showing aggregate data across all cultivation years"}
              color="secondary"
              variant="outlined"
            />
          )}
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                bgcolor: "#e8f5e8",
                cursor: "pointer",
                border: showPositive ? "2px solid #4caf50" : "1px solid #e0e0e0",
                height: "100%",
              }}
              onClick={() => setShowPositive(true)}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ArrowCircleUpIcon
                  sx={{ color: "#4caf50", fontSize: 48, mb: 1 }}
                />
                <Typography variant="h6" sx={{ mb: 0.5, fontSize: "1rem" }}>
                  Positive Impacts
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: "#4caf50", fontSize: "1.8rem" }}
                >
                  {displayData.positive.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                bgcolor: "#ffebee",
                cursor: "pointer",
                height: "100%",
                border: !showPositive ? "2px solid #f44336" : "1px solid #e0e0e0",
              }}
              onClick={() => setShowPositive(false)}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ArrowCircleDownIcon sx={{ color: "#f44336", fontSize: 48 }} />
                <Typography variant="h6" sx={{ mb: 0.5, fontSize: "1rem" }}>
                  Negative Impacts
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: "#f44336", fontSize: "1.8rem" }}
                >
                  {displayData.negative.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: "#e3f2fd", height: "100%" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <EuroIcon sx={{ color: "#2196f3", fontSize: 40 }}></EuroIcon>
                <Typography variant="h6">Net Benefit</Typography>
                <Typography variant="h4" sx={{ color: "#2196f3" }}>
                  {netBenefit
                    ? (() => {
                        const value = parseFloat(netBenefit.absDiff.value);
                        if (value >= 0) {
                          return `+${value.toFixed(2)} €/ha`;
                        } else {
                          return `-${Math.abs(value).toFixed(2)} €/ha`;
                        }
                      })()
                    : "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Impact Details */}
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Left side: Positive/Negative text */}
          {showPositive
            ? `Positive Impacts for ${selectedCrop || "Selected Crop"}`
            : `Negative Impacts for ${selectedCrop || "Selected Crop"}`}

          {/* Right side: year in bold if selected or only one individual year */}
          {(selectedYear !== "all" || individualYears.length === 1) && (
            <span style={{ fontWeight: "bold" }}>
              {selectedYear === AGGREGATE_YEAR_STR
                ? "All Years Aggregate"
                : selectedYear !== "all"
                ? selectedYear
                : individualYears[0]}
            </span>
          )}
        </Typography>

        <Divider
          sx={{
            marginBottom: 2,
            borderBottomWidth: 2.5,
            backgroundColor: showPositive ? "#51e551ff" : "#f37588ff",
          }}
        />

        <Grid container spacing={3}>
          {(showPositive ? displayData.positive : displayData.negative).map(
            (item, index) => (
              <Grid
                item
                xs={12}
                md={6}
                key={`${item.year}-${item.name}-${index}`}
              >
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    {/* Show year badge if multiple individual years and viewing all */}
                    {individualYears.length > 1 && selectedYear === "all" && (
                      <Chip
                        label={`Year: ${item.year}`}
                        size="small"
                        sx={{ mb: 1 }}
                        variant="outlined"
                      />
                    )}
                    <Box sx={{ textAlign: "left", mb: 2 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: "bold",
                          color: "text.primary",
                          mb: 0.5,
                        }}
                      >
                        {item.name.charAt(0).toUpperCase() +
                          item.name.slice(1).toLowerCase()}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <ImpactLevelIndicator impactLevel={item.impactLevel} />
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {item.subtitle}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        variant="h5"
                        component="span"
                        sx={{
                          color: getImpactColor(
                            item.absDiff,
                            item.higherIsBetter
                          ),
                          fontWeight: "bold",
                        }}
                      >
                        {`${item.absDiff > 0 ? "+" : ""}${item.absDiff?.toFixed(
                          2
                        )} ${formatDisplayUnit(item.unit)}`}
                      </Typography>
                      {typeof item.percDiff === "number" &&
                        !isNaN(item.percDiff) && (
                          <Chip
                            label={`${
                              item.percDiff > 0 ? "+" : ""
                            }${item.percDiff.toFixed(2)}%`}
                            variant="outlined"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )
          )}
        </Grid>
      </Box>
    );
  }

  function CostRevenueTab({ financialData, environmentalData, selectedCrop, cropArea }) {
    const [currentView, setCurrentView] = useState("financial"); // "financial" or "environmental"
    
    console.log(
      "CostRevenueTab data:",
      // financialData,
      // environmentalData,
      // selectedCrop,
      "cropArea:",
      cropArea
    );

    // Helper function to extract crop name from product value
    const extractCropName = (productValue) => {
      if (!productValue) return null;

      // Remove common suffixes like "crop", "plant", etc. and trim whitespace
      return productValue
        .replace(/\s+(crop|plant|cultivation|farming)\s*$/i, "")
        .trim()
        .toLowerCase();
    };

    // Helper function to check if unit contains "ha"
    const hasHaUnit = (unit) => {
      return unit && /\bha\b/i.test(String(unit));
    };

    // Helper function to apply crop area multiplication
    const applyAreaMultiplication = (value, unit, year) => {
    if (
        !hasHaUnit(unit) ||
        !cropArea ||
        value === null ||
        value === undefined
      ) {
        return value;
      }

      // For the aggregate year, use the average crop area across all available years
      if (year === AGGREGATE_YEAR) {
        const areas = Object.values(cropArea).filter(a => a != null && !isNaN(a));
        if (areas.length === 0) return value;
        const avgArea = areas.reduce((sum, a) => sum + a, 0) / areas.length;
        return value * avgArea;
      }

      // For individual years
      if (!cropArea[year]) return value;
      return value * cropArea[year];
    };

    // Parse SPARQL data
    const selectedCropNormalized = selectedCrop?.toLowerCase();
    console.log("Normalized selectedCrop:", selectedCropNormalized);

    const parseDataForCostRevenue = (data, productCropFilter) => {
      if (!data?.results?.bindings || !Array.isArray(data.results.bindings))
        return [];

      const normalizedFilter = productCropFilter?.toLowerCase();
      console.log("Normalized Filter:", normalizedFilter);

      return data.results.bindings
        .filter((item) => {
          if (!normalizedFilter) return true;

          // Extract and normalize the product value for comparison
          const extractedCropName = extractCropName(item.product?.value);
          console.log(
            "Original product:",
            item.product?.value,
            "Extracted:",
            extractedCropName
          );

          return extractedCropName === normalizedFilter;
        })
        .map((item) => ({
          name: item.name?.value,
          category: item.category?.value,
          unit: item.unit?.value,
          absDiff: parseFloat(item.absDiff?.value),
          description: item.description?.value || item.subtitle?.value,
          higherIsBetter: item.higherIsBetter?.value === "true",
          product: extractCropName(item.product?.value), // Store the cleaned crop name
          year: parseInt(item.year?.value),
        }));
    };

    // Parse environmental data
    const parseEnvironmentalData = (data, productCropFilter) => {
      if (!data?.results?.bindings || !Array.isArray(data.results.bindings))
        return [];

      const normalizedFilter = productCropFilter?.toLowerCase();

      return data.results.bindings
        .filter((item) => {
          if (!normalizedFilter) return true;
          const extractedCropName = extractCropName(item.product?.value);
          return extractedCropName === normalizedFilter;
        })
        .map((item) => ({
          name: item.name?.value,
          category: item.category?.value,
          unit: item.unit?.value,
          absDiff: parseFloat(item.absDiff?.value),
          description: item.description?.value || item.subtitle?.value,
          higherIsBetter: item.higherIsBetter?.value === "true",
          product: extractCropName(item.product?.value),
          year: parseInt(item.year?.value),
        }));
    };

    const parsedFinancialData = parseDataForCostRevenue(financialData, selectedCropNormalized);
    const parsedEnvironmentalData = parseEnvironmentalData(environmentalData, selectedCropNormalized);
    
    console.log("Parsed Financial Data", parsedFinancialData);
    console.log("Parsed Environmental Data", parsedEnvironmentalData);

    // Get current data based on selected view
    const currentData = currentView === "financial" ? parsedFinancialData : parsedEnvironmentalData;
    const currentDataSource = currentView === "financial" ? financialData : environmentalData;

    // Debug: Log current data and categories
    console.log(`Current view: ${currentView}`);
    console.log(`Current data length: ${currentData.length}`);
    if (currentData.length > 0) {
      const categories = [...new Set(currentData.map(item => item.category))];
      console.log("Categories in current data:", categories);
    }

    // Get all available years and sort them
    const availableYears = currentData.length > 0 
      ? [...new Set(currentData.map((item) => item.year))].sort((a, b) => a - b)
      : [new Date().getFullYear()];
    
    // Determine the current year (latest in the dataset) - still needed for some calculations
    const currentYear = availableYears.length > 0 ? Math.max(...availableYears) : new Date().getFullYear();
    
    console.log("Available years:", availableYears);
    console.log("Current year is", currentYear);

    // Group data by category and name
    const groupedData = currentData.reduce((acc, item) => {
      const key = `${item.category}-${item.name}`;
      if (!acc[key]) {
        acc[key] = {
          name: item.name,
          category: item.category,
          unit: item.unit,
          description: item.description,
          higherIsBetter: item.higherIsBetter,
          yearData: {},
        };
      }
      // Apply area multiplication based on unit and year
      const multipliedValue = applyAreaMultiplication(
        item.absDiff,
        item.unit,
        item.year
      );
      acc[key].yearData[item.year] = multipliedValue;
      return acc;
    }, {});

    // Separate data by category based on current view
    const getDataByCategory = () => {
      if (currentView === "financial") {
        const costItems = Object.values(groupedData).filter(
        (item) => item.category && item.category.includes("COSTS")
        );
        const revenueItems = Object.values(groupedData).filter(
          (item) => item.category === "OUTPUT"
        );
        const cbaItems = Object.values(groupedData).filter(
          (item) => item.category === "CBA"
        );
        
        return { costItems, revenueItems, cbaItems };
      } else {
        // Environmental data - group by different categories
        const environmentalItems = Object.values(groupedData).filter(
          (item) => item.category !== "COSTS" && item.category !== "OUTPUT" && item.category !== "CBA"
        );
        
        return { environmentalItems };
      }
    };

    const categoryData = getDataByCategory();

    // Calculate totals for financial view - now for all years
    const calculateFinancialTotals = () => {
      if (currentView !== "financial") return {};
      
      const totals = {};
      
      availableYears.forEach(year => {
        const costTotalForYear = (categoryData.costItems || []).reduce(
          (sum, item) => sum + (item.yearData[year] || 0),
          0
        );

        const revenueTotalForYear = (categoryData.revenueItems || []).reduce(
          (sum, item) => sum + (item.yearData[year] || 0),
          0
        );

        const netImpactForYear = revenueTotalForYear - costTotalForYear;

        totals[year] = {
          costTotal: costTotalForYear,
          revenueTotal: revenueTotalForYear,
          netImpact: netImpactForYear
        };
      });

      return totals;
    };

    const financialTotals = calculateFinancialTotals();

    // Format value for display
    const formatValue = (value) =>
      value === undefined || value === null || isNaN(value)
        ? "—"
        : currentView === "financial" 
          ? `${value.toFixed(2)}`
          : value.toFixed(2);

    // Helper function to get display unit (modify unit for area-multiplied values)
    const getDisplayUnit = (originalUnit, year) => {
      if (!hasHaUnit(originalUnit) || !cropArea || !cropArea[year]) {
        return formatDisplayUnit(originalUnit);
      }
      // Change €/ha to € when multiplied by area
      return formatDisplayUnit(
        String(originalUnit)
          .replace(/\s*\/\s*ha\b/i, "")
          .replace(/\s+per\s+ha\b/i, "")
      );
    };

    // Custom component for item description with info icon
    const ItemDescriptionWithInfo = ({ name, description }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{name}</span>
        {description && (
          <Tooltip
            title={description}
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 400,
                  fontSize: "0.875rem",
                  "& .MuiTooltip-arrow": {
                    color: "rgba(97, 97, 97, 0.9)",
                  },
                },
              },
            }}
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlined fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

    // MOVE useMemo BEFORE any early returns - this is the fix for the hook error
    const rows = useMemo(() => {
      if (currentData.length === 0) return [];

      let idCounter = 0;

      const makeRows = (items) =>
        items.map((item) => {
          const rowData = {
            id: idCounter++,
            category: item.category,
            name: item.name,
            nameWithInfo: (
              <ItemDescriptionWithInfo
                name={item.name}
                description={item.description}
              />
            ),
            unit: getDisplayUnit(item.unit, currentYear),
            description: item.description,
          };
          
          // Add columns for all available years
          availableYears.forEach(year => {
            rowData[year] = item.yearData[year] || null;
          });
          
          return rowData;
        });

      if (currentView === "financial") {
        const costRows = makeRows(categoryData.costItems || []);
        const revenueRows = makeRows(categoryData.revenueItems || []);
        const cbaRows = makeRows(categoryData.cbaItems || []);

        // Add totals for financial view
        const totalCostRow = {
          id: idCounter++,
          category: "COSTS TOTAL",
          name: "Total Costs",
          nameWithInfo: (
            <ItemDescriptionWithInfo
              name="Total Costs"
              description="Sum of all cost items"
            />
          ),
          unit: getDisplayUnit("€/ha", currentYear),
        };
        
        const totalRevenueRow = {
          id: idCounter++,
          category: "REVENUE TOTAL",
          name: "Total Revenue",
          nameWithInfo: (
            <ItemDescriptionWithInfo
              name="Total Revenue"
              description="Sum of all revenue items"
            />
          ),
          unit: getDisplayUnit("€/ha", currentYear),
        };

        // Add year data for total rows
        availableYears.forEach(year => {
          if (financialTotals[year]) {
            totalCostRow[year] = financialTotals[year].costTotal;
            totalRevenueRow[year] = financialTotals[year].revenueTotal;
          }
        });

        return [
          ...costRows,
          ...cbaRows,
          totalCostRow,
          ...revenueRows,
          totalRevenueRow
        ];
      } else {
        // Environmental view
        return makeRows(categoryData.environmentalItems || []);
      }
    }, [
      categoryData,
      financialTotals,
      availableYears,
      cropArea,
      currentView,
      currentData.length,
    ]);

    // NOW we can have early returns after all hooks are declared
    if (currentData.length === 0) {
      return (
        <Box sx={{ width: "100%" }}>
          {/* View Toggle Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3, gap: 1}}>
            <ButtonGroup variant="contained" aria-label="view toggle">
              <Button
                onClick={() => setCurrentView("financial")}
                variant={currentView === "financial" ? "contained" : "outlined"}
                startIcon={<EuroIcon />}
                sx={{ 
                  minWidth: 180,
                  textTransform: "none",
                  backgroundColor: currentView === "financial" ? "#1976d2" : "transparent",
                  color: currentView === "financial" ? "white" : "#1976d2",
                  "&:hover": {
                    backgroundColor: currentView === "financial" ? "#1565c0" : "#e3f2fd"
                  }
                }}
              >
                Financial Impact Report
              </Button>
              <Button
                onClick={() => setCurrentView("environmental")}
                variant={currentView === "environmental" ? "contained" : "outlined"}
                startIcon={<Nature />}
                sx={{ 
                  minWidth: 180,
                  textTransform: "none",
                  backgroundColor: currentView === "environmental" ? "#1976d2" : "transparent",
                  color: currentView === "environmental" ? "white" : "#1976d2",
                  "&:hover": {
                    backgroundColor: currentView === "environmental" ? "#1565c0" : "#e3f2fd"
                  }
                }}
              >
                Environmental & Agronomic KPIs
              </Button>
            </ButtonGroup>
          </Box>

          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No {currentView === "financial" ? "financial" : "environmental"} data available{" "}
              {selectedCrop
                ? "for " + selectedCrop[0].toUpperCase() + selectedCrop.slice(1)
                : ""}
            </Typography>
            {selectedCrop && currentDataSource?.results?.bindings && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Available products:{" "}
                {[
                  ...new Set(
                    currentDataSource.results.bindings
                      .map((item) => extractCropName(item.product?.value))
                      .filter(Boolean)
                  ),
                ].join(", ")}
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    // Create columns - now including all available years
    const columns = [];
    
    // Add category column only for financial view
    if (currentView === "financial") {
      columns.push({ field: "category", headerName: "Category", width: 150 });
    }
    
    // Add common columns
    columns.push({
      field: "nameWithInfo",
      headerName: "Item Description",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.value,
      sortable: false, // Disable sorting for this column since it contains JSX
    });
    
    columns.push({ field: "unit", headerName: "Unit", width: 100 });
    
    // Add columns for all available years
    // The aggregate-year column gets a descriptive header instead of just the year number
    availableYears.forEach(year => {
      columns.push({
        field: year.toString(),
        headerName: year === AGGREGATE_YEAR ? "All Years Aggregate" : year.toString(),
      renderHeader: year === AGGREGATE_YEAR 
        ? () => (
            <Box sx={{ lineHeight: 1.3, textAlign: "center", fontWeight: "bold" }}>
              <div>All Years</div>
              <div>Aggregate</div>
            </Box>
          )
        : undefined,
        type: "number",
        width: year === AGGREGATE_YEAR ? 180 : 140,
        valueFormatter: ({ value }) => formatValue(value),
        cellClassName: (params) => {
          if (params.value === null) return "";
          
          if (currentView === "financial") {
            const isRevenue =
              params.row.category && (
                params.row.category.includes("OUTPUT") ||
                params.row.category.includes("CBA") ||
                params.row.category.includes("REVENUE")
              );
            if (params.row.category && params.row.category.includes("COSTS")) {
              return params.value > 0 ? "cost-positive" : "cost-negative";
            } else if (isRevenue) {
              return params.value >= 0 ? "revenue-positive" : "revenue-negative";
            } else if (params.row.category === "NET IMPACT") {
              return params.value >= 0 ? "net-positive" : "net-negative";
            }
          } else {
            // Environmental view - you can customize this based on higherIsBetter logic
            return params.value >= 0 ? "env-positive" : "env-negative";
          }
          return "";
        },
      });
    });

    const getViewTitle = () => {
      const cropName = selectedCrop
        ? selectedCrop[0].toUpperCase() + selectedCrop.slice(1)
        : "Product";
      
      return currentView === "financial"
        ? `Financial Impact Report for ${cropName}`
        : `Environmental KPI & Agronomic Report for ${cropName}`;
    };

    return (
      <Box sx={{ width: "100%" }}>
        {/* View Toggle Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
          <ButtonGroup variant="contained" aria-label="view toggle">
          <Button
            onClick={() => setCurrentView("financial")}
            variant={currentView === "financial" ? "contained" : "outlined"}
            startIcon={<EuroIcon />}
            sx={{ 
              minWidth: 180,
              textTransform: "none",
              backgroundColor: currentView === "financial" ? "#1976d2" : "transparent",
              color: currentView === "financial" ? "white" : "#1976d2",
              "&:hover": {
                backgroundColor: currentView === "financial" ? "#1565c0" : "#e3f2fd"
              }
            }}
          >
            Financial Impact Report
          </Button>
          <Button
            onClick={() => setCurrentView("environmental")}
            variant={currentView === "environmental" ? "contained" : "outlined"}
            startIcon={<Nature />}
            sx={{ 
              minWidth: 180,
              textTransform: "none",
              backgroundColor: currentView === "environmental" ? "#1976d2" : "transparent",
              color: currentView === "environmental" ? "white" : "#1976d2",
              "&:hover": {
                backgroundColor: currentView === "environmental" ? "#1565c0" : "#e3f2fd"
              }
            }}
          >
            Environmental & Agronomic KPIs
          </Button>
          </ButtonGroup>
        </Box>

        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
        >
          {getViewTitle()}
        </Typography>

        {/* Show crop area information for all years */}
        {cropArea && Object.keys(cropArea).length > 0 && (
          <Typography
            variant="body2"
            sx={{ mb: 2, textAlign: "center", color: "text.secondary" }}
          >
            Crop Areas: {Object.entries(cropArea)
              .sort((a, b) => a[0] - b[0])
              .map(([year, area]) => `${year}: ${area} ha`)
              .join(", ")}
          </Typography>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[20, 30, 50]}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          autoHeight
          getRowClassName={(params) => {
            // Apply gray background to total rows (financial view only)
            if (
              currentView === "financial" &&
              ["COSTS TOTAL", "REVENUE TOTAL", "NET IMPACT"].includes(
                params.row.category
              )
            ) {
              return "total-row";
            }
            return "";
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              fontSize: 15,
            },
            "& .cost-positive": { color: "#f44336" },
            "& .cost-negative": { color: "#4caf50" },
            "& .revenue-positive": { color: "#4caf50" },
            "& .revenue-negative": { color: "#f44336" },
            "& .net-positive": { color: "#4caf50", fontWeight: "bold" },
            "& .net-negative": { color: "#f44336", fontWeight: "bold" },
            "& .env-positive": { color: "#2e7d32" },
            "& .env-negative": { color: "#d32f2f" },
            "& .total-row": {
              backgroundColor: "#f5f5f5", // light gray
              fontWeight: "bold",
            },
          }}
        />

        {/* Footnote for the aggregate summary column */}
        {availableYears.includes(AGGREGATE_YEAR) && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary", fontStyle: "italic" }}
          >
            * The <strong>All Years Aggregate </strong> column contains the aggregate
            across all available years and should not be compared directly with individual year columns.
          </Typography>
        )}
      </Box>
    );
  }

  function SustainabilityTab({
    aggregatedCategoriesData,
    sustainabilityCategoriesdData,
    selectedCrop,
  }) {
    // console.log("SustainabilityTab data:", aggregatedCategoriesData);
    // console.log("SustainabilityTab selectedCrop:", selectedCrop);

    // Add state for selected year - use "all" instead of null for initial state
    const [selectedYear, setSelectedYear] = useState("all");
    
    // Add state for chart type selection
    const [chartType, setChartType] = useState("indicators"); // "indicators" or "sustainability"

    const parseDataForChart = (data) => {
      if (!data?.results?.bindings || !Array.isArray(data.results.bindings)) {
        return [];
      }

      return data.results.bindings.map((item, index) => ({
        name: item.name?.value || "Unknown",
        product: item.product?.value || "Unknown",
        year: parseInt(item.year?.value) || 2024,
        percDiff: parseFloat(item.percDiff?.value) || 0,
        maxRangePercDiff: parseFloat(item.maxRangePercDiff?.value) || 0,
        unit: item.unit?.value || "percentage",
        id: index,
      }));
    };

    const parseSustainabilityData = (data) => {
      if (!data?.results?.bindings || !Array.isArray(data.results.bindings)) {
        return [];
      }

      return data.results.bindings.map((item, index) => ({
        name: item.name?.value || "Unknown",
        product: item.product?.value || "Unknown",
        year: parseInt(item.year?.value) || 2024,
        absDiff: parseFloat(item.absDiff?.value) || 0,
        percDiff: parseFloat(item.percDiff?.value) || 0,
        unit: item.unit?.value || "percentage",
        id: index,
      }));
    };

    const chartData = parseDataForChart(aggregatedCategoriesData);
    const sustainabilityData = parseSustainabilityData(sustainabilityCategoriesdData);

    // console.log("Parsed chartData:", chartData);
    // console.log("Parsed sustainability categories data:", sustainabilityData);

    // Get unique years from both datasets
    const allYears = [
      ...new Set([
        ...chartData.map((item) => item.year),
        ...sustainabilityData.map((item) => item.year)
      ])
    ].sort((a, b) => b - a); // Sort descending (most recent first)

    // Separate individual per-year entries from the convenience aggregate entry
    const individualYearsSust    = allYears.filter(y => y !== AGGREGATE_YEAR);
    const hasAggregateSummarySust = allYears.includes(AGGREGATE_YEAR);

    // REMOVED the problematic useEffect that was auto-setting the year

    // Get current dataset based on chart type
    const currentData = chartType === "indicators" ? chartData : sustainabilityData;

    // Filter data by crop first
    const cropFilteredData = useMemo(() => {
      if (selectedCrop && selectedCrop.trim() !== "") {
        return currentData.filter((item) => {
          const productName = (item.product || "").trim().toLowerCase();
          const selectedProduct = selectedCrop.trim().toLowerCase();
          console.log(`Comparing: "${productName}" with "${selectedProduct}"`);
          return (
            productName === selectedProduct ||
            productName.includes(selectedProduct)
          );
        });
      }
      return currentData;
    }, [currentData, selectedCrop]);

    // Then filter by year - handle "all" case
    const filteredData = useMemo(() => {
      if (selectedYear !== "all" && selectedYear !== null) {
        return cropFilteredData.filter((item) => item.year === selectedYear);
      }
      // "All Years" trend view must exclude the aggregate entry to avoid
      // a fake data point appearing at AGGREGATE_YEAR on the timeline
      return cropFilteredData.filter((item) => item.year !== AGGREGATE_YEAR);
    }, [cropFilteredData, selectedYear]);

    // console.log("Filtered data:", filteredData);

    // Get unique products and categories for the current chart
    const uniqueProducts = [...new Set(filteredData.map((item) => item.product))];
    const uniqueCategories = [...new Set(filteredData.map((item) => item.name))];
    const displayYears = [
      ...new Set(filteredData.map((item) => item.year)),
    ].sort();

    // console.log("Unique products:", uniqueProducts);
    // console.log("Unique categories:", uniqueCategories);

    // Handle year selection change
    const handleYearChange = (event) => {
      const value = event.target.value;
      setSelectedYear(value === "all" ? "all" : parseInt(value));
    };

    // Handle chart type change
    const handleChartTypeChange = (type) => {
      setChartType(type);
    };

    // Determine if we should show single year (scatter) or multi-year (line) chart
    const isMultiYear = selectedYear === "all" && displayYears.length > 1;

    // Prepare data for Highcharts - unified chart options
    const chartOptions = useMemo(() => {
      const isIndicators = chartType === "indicators";
      const chartTitle = isIndicators ? "Resource and Performance Indicators" : "Sustainability Parameters";
      const xAxisTitle = isIndicators ? "Resource Indicators Efficiency" : "Sustainability Parameters";

      if (isMultiYear) {
        // Multi-year line chart
        const series = uniqueCategories.map((category, categoryIndex) => {
          const categoryData = filteredData
            .filter((item) => item.name === category)
            .sort((a, b) => a.year - b.year);

          return {
            name: category,
            type: "line",
            data: categoryData.map((item) => ({
              x: item.year,
              y: item.percDiff,
              category: item.name,
              product: item.product,
              year: item.year,
              percDiff: item.percDiff,
              absDiff: item.absDiff,
              marker: {
                fillColor: item.percDiff >= 0 ? "#4caf50" : "#f44336",
                lineColor: item.percDiff >= 0 ? "#4caf50" : "#f44336",
              },
            })),
            color: isIndicators 
              ? ["#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8dd1e1", "#d084d0", "#a4de6c", "#ff8042"][categoryIndex % 8]
              : ["#2196f3", "#4caf50", "#ff9800", "#f44336", "#9c27b0", "#00bcd4", "#8bc34a", "#ff5722"][categoryIndex % 8],
            marker: {
              enabled: true,
              radius: 4,
            },
          };
        });

        return {
          chart: {
            type: "line",
            height: 400,
            backgroundColor: "transparent",
          },
          title: {
            text: `${chartTitle} Trends Over Time ${
              selectedCrop ? `for ${selectedCrop}` : ""
            }`,
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },
          xAxis: {
            type: "linear",
            title: {
              text: "Year",
            },
            tickInterval: 1,
            labels: {
              formatter: function () {
                return this.value;
              },
            },
          },
          yAxis: {
            title: {
              text: "Percentage Change (%)",
            },
          },
          tooltip: {
            formatter: function () {
              const sign = this.point.percDiff >= 0 ? "+" : "";
              let tooltipContent = `
                <b>${this.point.category}</b><br/>
                Product: ${this.point.product}<br/>
                Year: ${this.point.year}<br/>
                Change: ${sign}${this.point.percDiff.toFixed(2)}%<br/>
              `;
              
              // Add absolute difference for sustainability data
              if (!isIndicators && this.point.absDiff !== undefined) {
                tooltipContent += `Absolute: ${this.point.absDiff.toFixed(2)}`;
              }
              
              return tooltipContent;
            },
          },
          legend: {
            enabled: true,
          },
          plotOptions: {
            line: {
              marker: {
                enabled: true,
              },
            },
          },
          series: series,
        };
      } else {
        // Single year scatter chart
        const positiveData = [];
        const negativeData = [];

        uniqueProducts.forEach((product) => {
          const productData = filteredData.filter(
            (item) => item.product === product
          );

          productData.forEach((item) => {
            const point = {
              x: uniqueCategories.indexOf(item.name),
              y: item.percDiff,
              category: item.name,
              product: item.product,
              year: item.year,
              percDiff: item.percDiff,
              absDiff: item.absDiff,
              marker: {
                radius: 8,
              },
            };

            if (item.percDiff >= 0) {
              positiveData.push(point);
            } else {
              negativeData.push(point);
            }
          });
        });

        const series = [
          {
            name: "Positive Impact",
            type: "scatter",
            data: positiveData,
            color: "#4caf50",
            showInLegend: true,
            marker: {
              symbol: "circle",
            },
          },
          {
            name: "Negative Impact",
            type: "scatter",
            data: negativeData,
            color: "#f44336",
            showInLegend: true,
            marker: {
              symbol: "circle",
            },
          },
        ];

        return {
          chart: {
            type: "scatter",
            height: 450,
            backgroundColor: "transparent",
            marginRight: 150,
          },
          legend: {
            layout: "vertical",
            align: "right",
            verticalAlign: "middle",
            borderWidth: 0,
            itemStyle: {
              fontSize: "13px",
            },
          },
          title: {
            text: `${chartTitle} ${
              selectedCrop ? `for ${selectedCrop}` : ""
            } ${selectedYear !== "all"
              ? selectedYear === AGGREGATE_YEAR
                ? "— All Years Aggregate"
                : `(${selectedYear})`
              : ""}`,
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },
          xAxis: {
            categories: uniqueCategories,
            title: {
              text: xAxisTitle,
              margin: 30,
              style:{
                fontSize: "15px",
                fontWeight: "bold",
              }
            },
            labels: {
              rotation: 0,
              style: {
                fontSize: "12px",
              }
            }
          },
          yAxis: {
            title: {
              text: "Percentage Change (%)",
            },
          },
          tooltip: {
            formatter: function () {
              const sign = this.point.percDiff >= 0 ? "+" : "";
              let tooltipContent = `
                <b>${this.point.category}</b><br/>
                Product: ${this.point.product}<br/>
                Year: ${this.point.year}<br/>
                Change: ${sign}${this.point.percDiff.toFixed(2)}%<br/>
              `;
              
              // Add absolute difference for sustainability data
              if (!isIndicators && this.point.absDiff !== undefined) {
                tooltipContent += `Absolute: ${this.point.absDiff.toFixed(2)}`;
              }
              
              return tooltipContent;
            },
          },
          plotOptions: {
            scatter: {
              marker: {
                states: {
                  hover: {
                    enabled: true,
                    lineColor: "rgb(100,100,100)",
                  },
                },
              },
              events: {
                legendItemClick: function () {
                  return true; // Allow toggling
                },
              },
            },
          },
          series: [
            ...series,
            {
              name: isIndicators ? "No Data Baseline" : "Baseline",
              type: "line",
              color: "#666666",
              dashStyle: "LongDash",
              marker: { enabled: false },
              data: [
                [0, 0],
                [uniqueCategories.length - 1, 0],
              ],
              enableMouseTracking: false,
              showInLegend: true,
              zIndex: -10,
            },
          ],
        };
      }
    }, [
      filteredData,
      uniqueProducts,
      uniqueCategories,
      selectedCrop,
      selectedYear,
      isMultiYear,
      displayYears,
      chartType,
    ]);

    // Check if we have data for the current selection
    if (!filteredData.length) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ mb: 7, display: "flex", gap: 1, justifyContent: "center" }}>
            <ButtonGroup variant="contained" aria-label="view toggle">
            <Button
              variant={chartType === "indicators" ? "contained" : "outlined"}
              onClick={() => handleChartTypeChange("indicators")}
              startIcon={<Assessment />}
              color="primary"
              sx={{textTransform: "none"}}
            >
              Performance Indicators
            </Button>
            <Button
              variant={chartType === "sustainability" ? "contained" : "outlined"}
              onClick={() => handleChartTypeChange("sustainability")}
              startIcon={<Nature />}
              color="primary"
              sx={{textTransform: "none"}}
            >
              Sustainability Parameters
            </Button>
          </ButtonGroup>
        </Box>
          <Typography variant="h6" color="text.secondary">
            {selectedCrop && selectedYear !== "all"
              ? `No ${chartType} data available for ${selectedCrop} in ${selectedYear}`
              : selectedCrop
              ? `No ${chartType} data available for ${selectedCrop}`
              : selectedYear !== "all"
              ? `No ${chartType} data available for ${selectedYear}`
              : `No ${chartType} data available`}
          </Typography>
          {(selectedCrop || selectedYear !== "all") && currentData.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Available products:{" "}
              {[...new Set(currentData.map((item) => item.product))].join(", ")}
              <br />
              Available years: {allYears
                .map(y => y === AGGREGATE_YEAR ? "Three year analysis (2023–2025)" : y)
                .join(", ")}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box>
        {/* Chart Type Toggle Buttons */}
          <Box sx={{ mb: 3, display: "flex", gap: 1, justifyContent: "center" }}>
            <ButtonGroup variant="contained" aria-label="view toggle">
            <Button
              variant={chartType === "indicators" ? "contained" : "outlined"}
              onClick={() => handleChartTypeChange("indicators")}
              startIcon={<Assessment />}
              color="primary"
              sx={{textTransform: "none"}}
            >
              Performance Indicators
            </Button>
            <Button
              variant={chartType === "sustainability" ? "contained" : "outlined"}
              onClick={() => handleChartTypeChange("sustainability")}
              startIcon={<Nature />}
              color="primary"
              sx={{textTransform: "none"}}
            >
              Sustainability Parameters
            </Button>
          </ButtonGroup>
        </Box>

        {/* Year Filter */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="year-select-label">Select Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Select Year"
              onChange={handleYearChange}
            >
              <MenuItem value="all">All Years (Trends)</MenuItem>
              {individualYearsSust.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
              {hasAggregateSummarySust && (
                <MenuItem value={AGGREGATE_YEAR}>
                  {AGGREGATE_LABEL}
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Chart Type Indicator */}
          <Chip
            label={isMultiYear ? "Time Series View" : "Category Comparison"}
            color={isMultiYear ? "secondary" : "primary"}
            variant="outlined"
            sx={{ ml: 2 }}
            icon={isMultiYear ? <TrendingUp /> : <ScatterPlot />}
          />
        </Box>

        {/* Unified Chart */}
        <Box sx={{ mb: 3 }}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Box>
      </Box>
    );
  }

  function EnvironmentalStatisticsTab({ selectedCountry, selectedRegion }) {
    const [currentData, setCurrentData] = useState(null);
    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // console.log("Country selected:", selectedCountry);
    // console.log("Region selected:", selectedRegion);

    // Load JSON data based on selected country
    useEffect(() => {
      if (!selectedCountry) {
        setCurrentData(null);
        setCountryData(null);
        return;
      }

      setLoading(true);
      setError(null);

      // Dynamic import based on country name
      import(`../countries/${selectedCountry}.json`)
        .then((module) => {
          const loadedCountryData = module.default;
          setCountryData(loadedCountryData);

          // Navigate to the specific region"s Stats if region is selected and exists
          if (
            selectedRegion &&
            loadedCountryData[selectedRegion] &&
            loadedCountryData[selectedRegion].Stats
          ) {
            setCurrentData(loadedCountryData[selectedRegion].Stats);
          } else if (selectedRegion) {
            // Region selected but not found - show empty data
            setCurrentData({
              Landcover_stats: {},
              N2000_crop_stats: {},
              "N2000_LAU-COM_stats": {},
            });
          } else {
            // No region selected - use first available region
            const firstRegion = Object.keys(loadedCountryData).find(
              (key) =>
                key !== "Country" &&
                loadedCountryData[key] &&
                loadedCountryData[key].Stats
            );

            if (firstRegion) {
              setCurrentData(loadedCountryData[firstRegion].Stats);
            } else {
              setCurrentData(null);
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(`Failed to load data for ${selectedCountry}:`, err);
          setError(`Failed to load data for ${selectedCountry}`);
          setCurrentData(null);
          setCountryData(null);
          setLoading(false);
        });
    }, [selectedCountry, selectedRegion]);

    // Define a data transformation function
    function transformChartData(data) {
      if (!data || typeof data !== "object") {
        return [];
      }

      return Object.keys(data).map((key) => ({
        name: data[key].Label,
        y: data[key].percentage,
        description: data[key].Description,
      }));
    }

    // Helper function to get available regions for the selected country
    function getAvailableRegions() {
      if (!countryData) {
        return [];
      }

      return Object.keys(countryData).filter(
        (key) => key !== "Country" && countryData[key] && countryData[key].Stats
      );
    }

    // Show loading state
    if (loading) {
      return (
        <Card>
          <CardHeader
            title={`Environmental Statistics - ${selectedCountry}${
              selectedRegion ? `, ${selectedRegion}` : ""
            }`}
            sx={{
              color: error ? "white" : "inherit",
              m: 0,
              p: 0,
            }}
          />
          <Typography variant="body1" sx={{ p: 2, textAlign: "center" }}>
            Loading environmental data...
          </Typography>
        </Card>
      );
    }

    // Show error state
    if (error) {
      return (
        <Box>
          <Typography
            variant="body1"
            sx={{ p: 2, textAlign: "center", color: "error.main" }}
          >
            {error}
          </Typography>
        </Box>
      );
    }

    // Early return if no data available
    if (!currentData) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            No environmental data available
            {selectedRegion ? `, ${selectedRegion}` : ""}
          </Typography>
        </Box>
      );
    }

    const availableRegions = getAvailableRegions();

    console.log("Current data:", currentData);
    console.log(
      "Landcover stats:",
      currentData ? currentData["Landcover_stats"] : "No data"
    );
    console.log(
      "Transformed landcover data:",
      currentData ? transformChartData(currentData["Landcover_stats"]) : "No data"
    );
    console.log("Available regions:", availableRegions);

    const landCoverOptions = {
      chart: {
        type: "pie",
      },
      title: {
        text: "Land Cover Distribution",
      },

      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}</b>: ${this.point.description}`;
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.percentage:.1f} %",
          },
        },
      },
      series: [
        {
          name: "Land Cover",
          colorByPoint: true,
          data: transformChartData(currentData["Landcover_stats"]),
        },
      ],
    };

    // Horizontal Bar Chart Options
    const N200CropTypeOptions = {
      chart: {
        type: "bar",
      },
      title: {
        text: "Croptype protected under Natura 2000 areas",
      },
      xAxis: {
        type: "category",
        min: 0,
      },
      yAxis: {
        title: {
          text: "Percentage (%)",
        },
        max: 100,
      },
      tooltip: {
        formatter: function () {
          return `${this.point.description}: ${this.y.toFixed(1)} %`;
        },
      },
      series: [
        {
          name: "N2000 Croptype Stats",
          colorByPoint: true,
          data: transformChartData(currentData["N2000_crop_stats"]),
          showInLegend: false,
        },
      ],
    };

    const LauComOptions = {
      chart: {
        type: "bar",
      },
      title: {
        text: "Region protected under Natura 2000 areas",
      },
      xAxis: {
        type: "category",
        min: 0,
      },
      yAxis: {
        title: {
          text: "Percentage (%)",
        },
        max: 100,
      },
      tooltip: {
        formatter: function () {
          return `${this.point.description}: ${this.y.toFixed(1)} %`;
        },
      },
      series: [
        {
          name: "N2000 Lau Com Stats",
          colorByPoint: true,
          data: transformChartData(currentData["N2000_LAU-COM_stats"]),
          showInLegend: false,
        },
      ],
    };

    return (
      <Card>
        <HighchartsReact highcharts={Highcharts} options={landCoverOptions} />
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
            fontWeight: "bold",
          }}
        >
          Derived from: ESA WorldCover 10m 2021 V200 product. &nbsp;
          <Typography
            variant="body2"
            component="a"
            href="https://worldcover2021.esa.int/download"
            target="_blank"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Learn More
          </Typography>
        </Typography>
        <Divider
          sx={{
            border: "1px solid gray",
            mb: 2,
          }}
        />
        <HighchartsReact highcharts={Highcharts} options={N200CropTypeOptions} />
        <Divider
          sx={{
            border: "1px solid gray",
            mb: 2,
          }}
        />
        <HighchartsReact highcharts={Highcharts} options={LauComOptions} />
      </Card>
    );
  }

  export default function DATSImpactDashboard({
    test_case_id,
    selectedCrop,
    selectedCountry,
    selectedRegion,
    cropArea,
  }) {
    const [activeTab, setActiveTab] = useState(0);
    const [datsData, setDatsData] = useState({
      query1: [],
      query2: [],
      query3: [],
      query4: [],
      query5: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      if (!test_case_id) {
        setIsLoading(false); // Set to false so component shows normally
        return;
      }
      const fetchData = async () => {
        try {
          setIsLoading(true);

          const authString = btoa(
            `${REACT_APP_AUTH_USERNAME}:${REACT_APP_AUTH_PASSWORD}`
          );

          const response = await fetch(REACT_APP_TRIPLE_STORE_URL, {
            method: "POST",
            headers: {
              Authorization: `Basic ${authString}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              TESTCASE_ID: test_case_id?.match(/\d+/)[0],
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log("API Response:", result);

          if (result.success) {
            setDatsData(result.data);
          } else {
            throw new Error("Failed to fetch data");
          }
        } catch (err) {
          setError(err.message);
          console.error("Error fetching data:", err);
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }, [test_case_id]);

    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
    };

    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">Error: {error}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", mx: "auto", p: 1 }}>
        <Card sx={{ position: "sticky", top: 0 }}>
          <CardHeader
            title="DATS Regional Impact"
            sx={{
              bgcolor: "#DBD7D2",
              height: "3em",
              "& .MuiCardHeader-content": { my: 0 },
            }}
          />
          <CardContent>
            <TabContext value={activeTab}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  // Add these styles for horizontal scrolling
                  width: "100%",
                  overflow: "auto",
                  // Hide scrollbar for cleaner look (optional)
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                <TabList
                  onChange={handleTabChange}
                  aria-label="DATS impact tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 48,
                    // Ensure minimum width for tabs
                    "& .MuiTab-root": {
                      minWidth: {
                        xs: "auto", // Allow tabs to shrink on mobile
                        sm: 160, // Fixed width on larger screens
                      },
                      fontSize: {
                        xs: "0.75rem", // Smaller font on mobile
                        sm: "0.875rem", // Normal font on larger screens
                      },
                      px: {
                        xs: 1, // Less padding on mobile
                        sm: 2, // Normal padding on larger screens
                      },
                    },
                  }}
                >
                  <Tab label="DATS Impact by Category" />
                  <Tab label="Cost and Revenue Impact" />
                  <Tab label="Aggregated Sustainability Impact" />
                  <Tab label="Environmental Statistics" />
                </TabList>
              </Box>

              <TabPanel value={activeTab} index={0}>
                <CategoryImpactTab
                  data={datsData.query1}
                  selectedCrop={selectedCrop}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <CostRevenueTab
                  financialData={datsData.query2}
                  environmentalData={datsData.query5}
                  selectedCrop={selectedCrop}
                  cropArea={cropArea}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <SustainabilityTab
                  aggregatedCategoriesData={datsData.query3}
                  sustainabilityCategoriesdData={datsData.query4}
                  selectedCrop={selectedCrop}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <EnvironmentalStatisticsTab
                  selectedCountry={selectedCountry}
                  selectedRegion={selectedRegion}
                />
              </TabPanel>
            </TabContext>
          </CardContent>
        </Card>
      </Box>
    );
  }

  DATSImpactDashboard.propTypes = {
    test_case_id: PropTypes.string,
    selectedCrop: PropTypes.string,
  };
